"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { createClient } from "@/lib/supabase/client";
import { setupNativeSystemUi } from "@/lib/native/system-ui";
export type Theme = "orange" | "green" | "purple" | "red" | "pastel-mint" | "pastel-peach" | "pastel-lilac" | "pastel-rose";
export type Appearance = "light" | "dark" | "system";
type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultAppearance?: Appearance;
};
type ThemeContextType = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  appearance: Appearance;
  setAppearance: (appearance: Appearance) => void;
  canUseAppearanceModes: boolean;
  canUsePastelThemes: boolean;
};
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
const THEME_STORAGE_KEY = "coaching-calendar-theme";
const APPEARANCE_STORAGE_KEY = "coaching-calendar-appearance";
const DEFAULT_THEME: Theme = "red";
const DEFAULT_APPEARANCE: Appearance = "dark";
const BOLD_THEME_VALUES = ["orange", "green", "purple", "red"] as const;
const PASTEL_THEME_VALUES = ["pastel-mint", "pastel-peach", "pastel-lilac", "pastel-rose"] as const;
const PASTEL_THEME_TRANSITION_CLASS = "pastel-theme-transition";
const PASTEL_THEME_TRANSITION_MS = 220;
const THEMES_VALUES = [...BOLD_THEME_VALUES, ...PASTEL_THEME_VALUES] as const;
const THEME_CLASSES = THEMES_VALUES.map(theme => `theme-${theme}`);
function getUserScopedStorageKey(baseKey: string, userId: string | null): string {
  return userId ? `${baseKey}:${userId}` : baseKey;
}
function getSystemDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}
function readStoredTheme(key: string): Theme | null {
  if (typeof window === "undefined") return null;
  const value = localStorage.getItem(key);
  return value && THEMES_VALUES.includes(value as Theme) ? value as Theme : null;
}
export function isPastelTheme(theme: Theme): boolean {
  return PASTEL_THEME_VALUES.includes(theme as (typeof PASTEL_THEME_VALUES)[number]);
}
export function isThemeAllowed(theme: Theme, canUsePastelThemes: boolean): boolean {
  return canUsePastelThemes || !isPastelTheme(theme);
}
function readStoredAppearance(key: string): Appearance | null {
  if (typeof window === "undefined") return null;
  const value = localStorage.getItem(key);
  if (!value || !["light", "dark", "system"].includes(value)) return null;
  return value as Appearance;
}
function syncNativeStatusBar(wantDark: boolean): void {
  if (!Capacitor.isNativePlatform()) return;
  StatusBar.setStyle({
    style: wantDark ? Style.Dark : Style.Light
  }).catch(() => {});
}
export function ThemeProvider({
  children,
  defaultTheme = DEFAULT_THEME,
  defaultAppearance = DEFAULT_APPEARANCE
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [appearance, setAppearanceState] = useState<Appearance>(defaultAppearance);
  const [mounted, setMounted] = useState(false);
  const themeStorageKeyRef = useRef<string>(THEME_STORAGE_KEY);
  const appearanceStorageKeyRef = useRef<string>(APPEARANCE_STORAGE_KEY);
  const lastResolvedDarkRef = useRef<boolean | null>(null);
  const pastelThemeTransitionTimeoutRef = useRef<number | null>(null);

  // Keep the latest values available while auth-scoped storage keys resolve async.
  const latestThemeRef = useRef(theme);
  const latestAppearanceRef = useRef(appearance);
  useEffect(() => {
    latestThemeRef.current = theme;
  }, [theme]);
  useEffect(() => {
    latestAppearanceRef.current = appearance;
  }, [appearance]);
  const startPastelThemeTransition = useCallback((root: HTMLElement) => {
    if (!isPastelTheme(latestThemeRef.current)) return;
    if (pastelThemeTransitionTimeoutRef.current !== null) {
      window.clearTimeout(pastelThemeTransitionTimeoutRef.current);
    }
    root.classList.add(PASTEL_THEME_TRANSITION_CLASS);
    pastelThemeTransitionTimeoutRef.current = window.setTimeout(() => {
      root.classList.remove(PASTEL_THEME_TRANSITION_CLASS);
      pastelThemeTransitionTimeoutRef.current = null;
    }, PASTEL_THEME_TRANSITION_MS);
  }, []);

  // Read theme and appearance from localStorage on mount
  useEffect(() => {
    let cancelled = false;
    const storedBootstrapTheme = readStoredTheme(THEME_STORAGE_KEY);
    const initialTheme = storedBootstrapTheme ?? DEFAULT_THEME;
    const initialAppearance = readStoredAppearance(APPEARANCE_STORAGE_KEY) ?? DEFAULT_APPEARANCE;
    const initialStateRafId = window.requestAnimationFrame(() => {
      if (cancelled) return;
      if (initialTheme) {
        setThemeState(initialTheme);
      }
      if (initialAppearance) {
        setAppearanceState(initialAppearance);
      }
      setMounted(true);
    });
    const hydrateThemeState = async () => {
      let userId: string | null = null;
      try {
        const supabase = createClient();
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        userId = session?.user?.id ?? null;
      } catch {
        userId = null;
      }
      const resolvedThemeStorageKey = getUserScopedStorageKey(THEME_STORAGE_KEY, userId);
      const resolvedAppearanceStorageKey = getUserScopedStorageKey(APPEARANCE_STORAGE_KEY, userId);
      if (cancelled) return;
      themeStorageKeyRef.current = resolvedThemeStorageKey;
      appearanceStorageKeyRef.current = resolvedAppearanceStorageKey;
      const storedTheme = readStoredTheme(resolvedThemeStorageKey);
      if (storedTheme) {
        setThemeState(storedTheme);
      } else {
        const bootstrapTheme = readStoredTheme(THEME_STORAGE_KEY);
        const latestTheme = latestThemeRef.current;
        const fallbackTheme = [bootstrapTheme, latestTheme, DEFAULT_THEME].find((candidate): candidate is Theme => !!candidate) ?? DEFAULT_THEME;
        setThemeState(fallbackTheme);
        localStorage.setItem(resolvedThemeStorageKey, fallbackTheme);
        localStorage.setItem(THEME_STORAGE_KEY, fallbackTheme);
      }
      const storedAppearance = readStoredAppearance(resolvedAppearanceStorageKey);
      if (storedAppearance) {
        setAppearanceState(storedAppearance);
      } else {
        const fallbackAppearance = readStoredAppearance(APPEARANCE_STORAGE_KEY) ?? latestAppearanceRef.current ?? DEFAULT_APPEARANCE;
        localStorage.setItem(resolvedAppearanceStorageKey, fallbackAppearance);
      }
    };
    void hydrateThemeState();
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(initialStateRafId);
      if (pastelThemeTransitionTimeoutRef.current !== null) {
        window.clearTimeout(pastelThemeTransitionTimeoutRef.current);
        document.documentElement.classList.remove(PASTEL_THEME_TRANSITION_CLASS);
      }
    };
  }, []);

  // Apply theme (accent) class to document whenever theme changes
  useEffect(() => {
    if (!mounted) return;
    const root_0 = document.documentElement;
    root_0.classList.remove(...THEME_CLASSES);
    root_0.classList.add(`theme-${theme}`);
    localStorage.setItem(themeStorageKeyRef.current, theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme, mounted]);

  // Apply .dark class based on appearance (light | dark | system)
  useEffect(() => {
    if (!mounted) return;
    const root_1 = document.documentElement;
    const resolvedAppearance = appearance;
    const wantDark = resolvedAppearance === "dark" || resolvedAppearance === "system" && getSystemDark();
    localStorage.setItem(appearanceStorageKeyRef.current, resolvedAppearance);
    localStorage.setItem(APPEARANCE_STORAGE_KEY, resolvedAppearance);
    if (lastResolvedDarkRef.current !== null && lastResolvedDarkRef.current !== wantDark) {
      startPastelThemeTransition(root_1);
    }
    lastResolvedDarkRef.current = wantDark;
    if (wantDark) {
      root_1.classList.add("dark");
    } else {
      root_1.classList.remove("dark");
    }
  }, [appearance, mounted, startPastelThemeTransition]);

  // When appearance is "system", react to OS preference changes
  useEffect(() => {
    if (!mounted || appearance !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const root_2 = document.documentElement;
      if (lastResolvedDarkRef.current !== null && lastResolvedDarkRef.current !== mq.matches) {
        startPastelThemeTransition(root_2);
      }
      lastResolvedDarkRef.current = mq.matches;
      if (mq.matches) {
        root_2.classList.add("dark");
      } else {
        root_2.classList.remove("dark");
      }
      syncNativeStatusBar(mq.matches);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [appearance, mounted, startPastelThemeTransition]);

  // Capacitor: sync status bar style (clock, battery, etc.) with dark/light mode
  useEffect(() => {
    if (!mounted || !Capacitor.isNativePlatform()) return;
    const resolvedAppearance_0 = appearance;
    const wantDark_0 = resolvedAppearance_0 === "dark" || resolvedAppearance_0 === "system" && getSystemDark();
    syncNativeStatusBar(wantDark_0);
  }, [mounted, appearance]);
  useEffect(() => {
    if (!mounted) return;
    void setupNativeSystemUi();
  }, [mounted]);
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };
  const setAppearance: ThemeContextType["setAppearance"] = newAppearance => {
    const nextAppearance = newAppearance;
    setAppearanceState(nextAppearance);
    localStorage.setItem(appearanceStorageKeyRef.current, nextAppearance);
    localStorage.setItem(APPEARANCE_STORAGE_KEY, nextAppearance);
    const root_3 = document.documentElement;
    const wantDark_1 = nextAppearance === "dark" || nextAppearance === "system" && getSystemDark();
    if (lastResolvedDarkRef.current !== null && lastResolvedDarkRef.current !== wantDark_1) {
      startPastelThemeTransition(root_3);
    }
    lastResolvedDarkRef.current = wantDark_1;
    if (wantDark_1) {
      root_3.classList.add("dark");
    } else {
      root_3.classList.remove("dark");
    }
    syncNativeStatusBar(wantDark_1);
  };
  const value = {
    theme: mounted ? theme : defaultTheme,
    setTheme,
    appearance: mounted ? appearance : defaultAppearance,
    setAppearance,
    canUseAppearanceModes: true,
    canUsePastelThemes: true
  };
  return <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>;
}
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

// Theme-based color classes for measurement UI (shared by body/measurements and daily routine)
export const THEME_STYLES: Record<Theme, {
  selectedBg: string;
  selectedRing: string;
  selectedText: string;
  selectedValueText: string;
  chartGradient: string;
}> = {
  green: {
    selectedBg: "bg-green-600",
    selectedRing: "ring-green-400",
    selectedText: "text-green-600",
    selectedValueText: "text-green-700",
    chartGradient: "from-green-50 to-emerald-50"
  },
  orange: {
    selectedBg: "bg-orange-500",
    selectedRing: "ring-orange-400",
    selectedText: "text-orange-600",
    selectedValueText: "text-orange-700",
    chartGradient: "from-orange-50 to-amber-50"
  },
  purple: {
    selectedBg: "bg-purple-600",
    selectedRing: "ring-purple-400",
    selectedText: "text-purple-600",
    selectedValueText: "text-purple-700",
    chartGradient: "from-purple-50 to-violet-50"
  },
  red: {
    selectedBg: "bg-red-600",
    selectedRing: "ring-red-400",
    selectedText: "text-red-600",
    selectedValueText: "text-red-700",
    chartGradient: "from-red-50 to-rose-50"
  },
  "pastel-mint": {
    selectedBg: "bg-teal-500",
    selectedRing: "ring-teal-300",
    selectedText: "text-teal-600",
    selectedValueText: "text-teal-700",
    chartGradient: "from-teal-50 to-emerald-50"
  },
  "pastel-peach": {
    selectedBg: "bg-orange-400",
    selectedRing: "ring-orange-200",
    selectedText: "text-orange-500",
    selectedValueText: "text-orange-600",
    chartGradient: "from-orange-50 to-rose-50"
  },
  "pastel-lilac": {
    selectedBg: "bg-violet-400",
    selectedRing: "ring-violet-200",
    selectedText: "text-violet-500",
    selectedValueText: "text-violet-600",
    chartGradient: "from-violet-50 to-fuchsia-50"
  },
  "pastel-rose": {
    selectedBg: "bg-rose-400",
    selectedRing: "ring-rose-200",
    selectedText: "text-rose-500",
    selectedValueText: "text-rose-600",
    chartGradient: "from-rose-50 to-pink-50"
  }
};

// Theme metadata for UI
export const THEMES = [{
  value: "green" as Theme,
  label: "Nature Green",
  color: "#16a34a"
}, {
  value: "orange" as Theme,
  label: "Energetic Orange",
  color: "#f97316"
}, {
  value: "purple" as Theme,
  label: "Royal Purple",
  color: "#9333ea"
}, {
  value: "red" as Theme,
  label: "Bold Red",
  color: "#dc2626"
}, {
  value: "pastel-mint" as Theme,
  label: "Pastel Mint",
  color: "#8dd8c8"
}, {
  value: "pastel-peach" as Theme,
  label: "Pastel Peach",
  color: "#f2b79e"
}, {
  value: "pastel-lilac" as Theme,
  label: "Pastel Lilac",
  color: "#c8b5ee"
}, {
  value: "pastel-rose" as Theme,
  label: "Pastel Rose",
  color: "#efaec3"
}] as const;
export const BOLD_THEMES = THEMES.filter(theme => !isPastelTheme(theme.value));
export const PASTEL_THEMES = THEMES.filter(theme => isPastelTheme(theme.value));
export function getAvailableThemes(canUsePastelThemes: boolean) {
  return canUsePastelThemes ? THEMES : BOLD_THEMES;
}